import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Globe2, Sparkles, Zap } from "lucide-react";
import api from "../lib/api";
import { BackToWrestler } from "../components/BackToWrestler";
import { TourMap } from "../components/tour/TourMap";
import { TourBench } from "../components/tour/TourBench";
import { TourUpgradesModal } from "../components/tour/TourUpgradesModal";
import { TourCurrencyStrip } from "../components/tour/TourCurrencyStrip";
import { TourPlaneModal } from "../components/tour/TourPlaneModal";
import { visibleInterval } from "../lib/visibleInterval";
import { useInZoom } from "../components/MobileZoom";
import { TourTutorial } from "../components/tour/TourTutorial";

function useTourFloats() {
  const [floats, setFloats] = useState({});
  const keyRef = useRef(0);
  const push = useCallback((state) => {
    const byWid = Object.fromEntries((state.locations || []).filter((l) => l.wrestler_id).map((l) => [l.wrestler_id, l.id]));
    const add = {};
    Object.entries(state.ticks || {}).forEach(([wid, t]) => {
      const lid = byWid[wid];
      if (!lid || !(t.amount > 0)) return;
      add[lid] = { key: ++keyRef.current, ...t };
    });
    (state.deliveries || []).forEach((d) => {
      if (d.amount > 0) add[d.location_id] = { key: ++keyRef.current, currency: "fans", amount: d.amount, plane: true };
    });
    if (!Object.keys(add).length) return;
    setFloats((prev) => {
      const next = { ...prev };
      Object.entries(add).forEach(([lid, f]) => { next[lid] = [...(next[lid] || []).slice(-2), f]; });
      return next;
    });
  }, []);
  return [floats, push];
}

// Gains auto-claimed when entering the tour from a wrestler page (passed via router state).
function useClaimPops() {
  const location = useLocation();
  const [pops, setPops] = useState(() => location.state?.claimed || {});
  useEffect(() => {
    if (!Object.keys(pops).length) return;
    window.history.replaceState({}, "");
    const t = setTimeout(() => setPops({}), 3600);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return pops;
}

function ActiveInfo({ active }) {
  return (
    <div className="font-mono text-[11px] text-[#A89BCC] flex items-center justify-center gap-2 text-center" data-testid="tour-active-info">
      <Zap size={13} className="text-[#39FF14] shrink-0" />
      <span>{active
        ? <>Active: <span className="text-[#39FF14]">{active.name}</span> at full speed ({active.gain_active.toFixed(2)} fans/s). Others farm passively. Tap a venue's wrestler to switch.</>
        : <>Book wrestlers at venues. The active wrestler farms at full speed while you're on this screen; the rest farm passively.</>}</span>
    </div>
  );
}

export default function TourPage() {
  const [state, setState] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [showUpgrades, setShowUpgrades] = useState(false);
  const [busy, setBusy] = useState(false);
  const [floats, pushFloats] = useTourFloats();
  const pops = useClaimPops();
  const inZoom = useInZoom();
  const inFlight = useRef(false);

  const pending = useRef(false);
  const load = useCallback(async () => {
    if (inFlight.current) { pending.current = true; return; }
    inFlight.current = true;
    try {
      const r = await api.get("/tour/state");
      setState(r.data);
      pushFloats(r.data);
    } catch { /* keep last state */ } finally {
      inFlight.current = false;
      if (pending.current) { pending.current = false; load(); }
    }
  }, [pushFloats]);

  useEffect(() => {
    load();
    return visibleInterval(load, 2000);
  }, [load]);

  const act = async (fn) => {
    try { await fn(); await load(); return true; } catch (e) { toast.error(e.response?.data?.detail || "Failed"); return false; }
  };
  const place = (wid, lid) => { setSelectedId(null); act(() => api.post("/tour/place", { wrestler_id: wid, location_id: lid })); };
  const onDragStart = (e, wid) => { e.dataTransfer.setData("text/wrestler", wid); e.dataTransfer.effectAllowed = "move"; };
  const onClickLocation = (loc) => {
    if (selectedId) return selectedId === loc.wrestler_id ? setSelectedId(null) : place(selectedId, loc.id);
    if (loc.wrestler_id) {
      if (inZoom) setSelectedId(loc.wrestler_id);
      act(() => api.post(`/tour/set-active/${loc.wrestler_id}`));
    }
  };
  const onBenchTap = () => {
    const sel = state?.assigned.find((w) => w.wrestler_id === selectedId);
    if (inZoom && sel?.tour_location) place(sel.wrestler_id, null);
  };
  const buy = async (id) => { setBusy(true); const ok = await act(() => api.post(`/tour/upgrades/${id}/buy`)); setBusy(false); return ok; };
  const [planeId, setPlaneId] = useState(null);
  const [tut, setTut] = useState(null);
  useEffect(() => { if (state?.tutorial) setTut(state.tutorial); }, [state?.tutorial]);
  const planeAct = (path, body) => act(() => api.post(`/tour/planes/${planeId}/${path}`, body));

  if (!state) return <div className="p-8 font-mono text-[#A89BCC]" data-testid="tour-loading">Booking the tour bus…</div>;
  const bench = state.assigned.filter((w) => !w.tour_location);
  const active = state.assigned.find((w) => w.is_active && w.tour_location);
  const affordable = state.upgrades.filter((u) => u.unlocked && u.affordable && !u.requires_missing).length;
  const plane = (state.planes || []).find((p) => p.id === planeId);

  return (
    <div className="max-w-[1260px] mx-auto px-3 py-3 space-y-3" data-testid="tour-page">
      {tut && (
        <TourTutorial stage={tut} benchSelector={bench[0] ? `[data-testid="tour-bench-${bench[0].wrestler_id}"]` : null}
                      onReward={async () => { try { await api.post("/tour/tutorial/reward"); await load(); } catch {} }}
                      onDone={() => setTut(null)} />
      )}
      <div className="relative rounded-2xl overflow-hidden border-2 border-[#00F0FF]/30 shadow-[0_0_40px_rgba(0,240,255,0.12)] bg-[#0A051A] bg-cover bg-center"
           style={{ containerType: "inline-size", backgroundImage: "url('/tour/World_Tour_Background.webp')", height: "clamp(calc(min(var(--vw, 1vw) * 100 - 24px, 1236px) * 0.448), calc(var(--vh, 1vh) * 100 - 340px), calc(min(var(--vw, 1vw) * 100 - 24px, 1236px) * 0.5124))" }} data-testid="tour-map-block">
        <div className="absolute" style={{ width: "112%", left: "-6%", top: "50%", transform: "translateY(calc(-52% + 30px))" }} data-testid="tour-map-zoom">
          <TourMap state={state} floats={floats} pops={pops} selectedId={selectedId} onDropWrestler={place}
                   onClickLocation={onClickLocation} onDragStartPlaced={onDragStart} />
        </div>
        <div className="absolute top-0 inset-x-0 z-[15] pointer-events-none" style={{ height: "5cqw", background: "linear-gradient(to bottom, rgba(10,5,26,0.75), transparent)" }} />
        <div className="absolute inset-0 z-20 pointer-events-none" data-testid="tour-map-header">
          <BackToWrestler testId="tour-back-btn" className="pointer-events-auto absolute !px-[0.8em] !py-[0.3em] !gap-[0.4em] !border"
                          style={{ fontSize: "0.95cqw", left: "0.7cqw", top: "0.7cqw" }} />
          <div className="absolute flex items-center" style={{ left: "18.5%", top: "0.7cqw", gap: "1.2cqw" }}>
            <div className="flex items-center" style={{ gap: "0.6cqw" }}>
              <Globe2 className="text-[#00F0FF] animate-[spin_18s_linear_infinite]" style={{ width: "1.7cqw", height: "1.7cqw", filter: "drop-shadow(0 0 8px #00F0FF)" }} />
              <h1 className="font-heading uppercase leading-none whitespace-nowrap" style={{ fontSize: "1.8cqw" }}><span className="neon-pink">World</span> <span className="neon-cyan">Tour</span></h1>
            </div>
            <TourCurrencyStrip fans={state.fans} handshakes={state.handshakes} fluid className="pointer-events-auto !flex-nowrap"
                               style={{ fontSize: "0.95cqw", gap: "0.4cqw" }} />
          </div>
        </div>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, transparent 60%, rgba(10,5,26,0.7) 100%)" }} />
        <div className="absolute bottom-0 inset-x-0 z-20 flex justify-center pb-3 pointer-events-none">
          <button data-testid="tour-upgrades-btn" onClick={() => setShowUpgrades(true)}
                  className="pointer-events-auto relative btn-neon px-6 py-2 flex items-center gap-2 text-sm shadow-[0_0_20px_rgba(255,0,127,0.45)]">
            <Sparkles size={16} /> Upgrades
            {affordable > 0 && <span data-testid="tour-upgrades-badge" className="absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full bg-[#39FF14] text-[#0A051A] font-mono text-[10px] font-bold flex items-center justify-center animate-pulse">{affordable}</span>}
          </button>
        </div>
      </div>
      <TourBench wrestlers={bench} selectedId={selectedId} pops={pops} planes={state.planes} onOpenPlane={setPlaneId}
                 onSelect={(id) => setSelectedId((s) => (s === id ? null : id))}
                 onDragStart={onDragStart} onDropToBench={(wid) => place(wid, null)} onBenchTap={onBenchTap} />
      <ActiveInfo active={active} />
      {showUpgrades && <TourUpgradesModal state={state} onClose={() => setShowUpgrades(false)} onBuy={buy} busy={busy} />}
      {plane && <TourPlaneModal plane={plane} state={state} onClose={() => setPlaneId(null)}
                                onUpgrade={(stat) => planeAct("upgrade", { stat })} onToggle={() => planeAct("toggle")}
                                onRoute={(lid) => planeAct("route", { location_id: lid })} />}
    </div>
  );
}
